import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Search,
  Printer,
  Receipt,
  Loader2,
  FileSearch,
  ChevronLeft,
  ChevronRight,
  UserRound,
  Wallet,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { StatusPill } from '@/components/pos/StatusPill';
import { MoneyValue } from '@/components/pos/MoneyValue';
import { EmptyState } from '@/components/pos/EmptyState';
import { ReceiptPreview } from '@/components/pos/ReceiptPreview';
import { ResumePaymentDialog } from '@/components/pos/ResumePaymentDialog';
import { DatePickerField } from '@/components/pos/DatePickerField';
import { useOrderLookup } from '@/hooks/queries';
import { useSaleDetail, usePrintBatch, useReprintSingle } from '@/hooks/mutations';
import { useDeviceStore } from '@/stores/device.store';
import { useHotkeys } from '@/hooks/useHotkeys';
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner';
import { formatDateTime, todayISO } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { SaleDetail, SaleLookupRow } from '@/types/sale';

const PER_PAGE = 8;

const PAYMENT_LABEL: Record<string, string> = {
  cash: 'Cash',
  midtrans: 'Midtrans',
  'e-money': 'E-Money',
  emoney: 'E-Money',
  qris: 'QRIS',
};
const CHANNEL_LABEL: Record<string, string> = {
  ticketbox: 'Ticketbox',
  online: 'Online',
  kiosk: 'Kiosk',
  travel_agent: 'Travel Agent',
  manless: 'Manless',
};
const PRINT_STATUS_LABEL: Record<string, string> = {
  PENDING: 'Belum dicetak',
  PRINTING: 'Sebagian dicetak',
  PRINTED: 'Tercetak',
  FAILED: 'Gagal cetak',
};

function printChipClass(s?: string | null): string {
  switch (s) {
    case 'PRINTED':
      return 'bg-green-100 text-green-700';
    case 'PRINTING':
      return 'bg-amber-100 text-amber-700';
    case 'FAILED':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-slate-100 text-slate-600'; // PENDING / unknown
  }
}

function paymentChipClass(g?: string | null): string {
  switch ((g || '').toLowerCase()) {
    case 'cash':
      return 'bg-green-100 text-green-700';
    case 'midtrans':
      return 'bg-blue-100 text-blue-700';
    case 'e-money':
    case 'emoney':
      return 'bg-emerald-100 text-emerald-700';
    case 'qris':
      return 'bg-violet-100 text-violet-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
}

const CHIP_BASE = 'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium';

export function OrderLookupPage() {
  // Seed the search from ?q= (e.g. a recent order tapped on Beranda), so the
  // invoice lands pre-filtered and ready to reprint.
  const [searchParams] = useSearchParams();
  const initialQ = searchParams.get('q') ?? '';
  const [term, setTerm] = useState(initialQ);
  const [activeQuery, setActiveQuery] = useState(initialQ);
  // Empty = all dates; pick a day to narrow.
  const [date, setDate] = useState('');
  // 'own' = this operator's orders (default); 'all' = every cashier's.
  const [scope, setScope] = useState<'own' | 'all'>('own');
  const [page, setPage] = useState(1);
  const printerBound = useDeviceStore((s) => Boolean(s.ticketPrinterId));

  // The list shows the operator's orders (or all with the toggle); `date`
  // narrows to a day, `q` refines by invoice / name / phone.
  const { data: lookup, isFetching, refetch: refetchOrders } = useOrderLookup({
    q: activeQuery,
    date,
    page,
    perPage: PER_PAGE,
    scope,
  });
  const rows = lookup?.rows ?? [];
  const total = lookup?.total ?? 0;
  const totalPage = lookup?.totalPage ?? 1;

  const { mutate: fetchDetail, isPending: detailLoading } = useSaleDetail();
  const { mutate: printBatch, isPending: printing } = usePrintBatch();
  const { mutate: reprintOne } = useReprintSingle();

  const [selected, setSelected] = useState<SaleDetail | null>(null);

  // Any filter change returns to the first page.
  useEffect(() => {
    setPage(1);
  }, [activeQuery, date, scope]);

  const run = (q: string) => {
    const trimmed = q.trim();
    // Mirror the searched value into the box too, so a scan that fires while
    // the input isn't focused still shows what was looked up.
    setTerm(trimmed);
    setActiveQuery(trimmed);
  };

  // Clear the search box AND the active query so the list drops back to the
  // full (filtered-by-date/scope) view.
  const clearSearch = () => {
    setTerm('');
    setActiveQuery('');
  };

  const openDetail = (row: SaleLookupRow) => {
    fetchDetail(row.invoiceId, { onSuccess: (d) => setSelected(d) });
  };

  const reprint = () => {
    if (!selected) return;
    if (!printerBound) {
      toast.warning('Printer belum diatur — tidak dapat mencetak ulang.');
      return;
    }
    const blocked = ['CANCELLED', 'REFUND'].includes(String(selected.status));
    if (blocked) {
      toast.error('Tiket dari transaksi yang dibatalkan/refund tidak dapat dicetak ulang.');
      return;
    }
    printBatch(selected.invoiceId, {
      onSuccess: (res) => {
        const ok = res.tickets.filter((t) => t.status === 'PRINTED').length;
        toast.success(`${ok}/${res.tickets.length} tiket dicetak ulang.`);
      },
      onError: (err) => toast.error(err.message),
    });
  };

  useHotkeys({ 'ctrl+p': reprint });
  // Auto-search on scan even without a trailing Enter: `flushOnIdleMs` fires the
  // lookup once the scan burst goes quiet (human typing never triggers it).
  useBarcodeScanner({ onScan: (code) => run(code), flushOnIdleMs: 120 });

  // An order still awaiting payment can be finished from here. INITIAL is
  // included deliberately: a Snap sale starts INITIAL, so excluding it would
  // hide exactly the sales most likely to have been abandoned mid-payment.
  // Only midtrans can be continued from here. A QRIS/Snap charge lives at the
  // gateway until it expires, so re-opening it re-shows the SAME code and
  // moves no money — e-money would have to re-arm a reader and EDC has its own
  // resolve flow. The server refuses those anyway; not offering the button is
  // just not promising something it will then decline.
  const unpaid =
    Boolean(selected) &&
    ['PENDING', 'INITIAL'].includes(String(selected?.status)) &&
    String(selected?.paymentGateway) === 'midtrans';
  const [resumeOpen, setResumeOpen] = useState(false);

  const reprintDisabled =
    !selected || !printerBound || ['CANCELLED', 'REFUND'].includes(String(selected.status));

  // Collapse identical tickets (same product + visitor type) into one reprint
  // control so N adult tickets show as a single "×N" button instead of N
  // look-alike rows. The button reprints every ticket in the group.
  const ticketGroups = useMemo(() => {
    const groups: Array<{ key: string; label: string; ids: string[] }> = [];
    const byKey = new Map<string, number>();
    for (const t of selected?.tickets ?? []) {
      const label = t.productName ?? t.id;
      const key = `${label}|${t.visitorType ?? ''}`;
      const at = byKey.get(key);
      if (at == null) {
        byKey.set(key, groups.length);
        groups.push({ key, label, ids: [t.id] });
      } else {
        groups[at]!.ids.push(t.id);
      }
    }
    return groups;
  }, [selected]);

  // Client-side receipt print to a thermal printer. The `printing-receipt`
  // class scopes the print CSS (80mm roll, monochrome — see index.css) to just
  // the receipt; cleared on afterprint (with a timeout fallback). Prints
  // SILENTLY (no dialog) when Chrome runs with --kiosk-printing and the thermal
  // printer is the default; otherwise the normal print dialog opens.
  const printReceipt = () => {
    if (!selected) return;
    const root = document.documentElement;
    root.classList.add('printing-receipt');
    const cleanup = () => {
      root.classList.remove('printing-receipt');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
    setTimeout(cleanup, 1500);
  };

  const reprintGroup = (ids: string[], label: string) => {
    let done = 0;
    ids.forEach((id) =>
      reprintOne(id, {
        onSuccess: () => {
          done += 1;
          if (done === ids.length) toast.success(`${label} dicetak ulang (${ids.length}).`);
        },
        onError: (err) => toast.error(err.message),
      }),
    );
  };

  return (
    <div className="grid h-full grid-cols-1 gap-6 px-6 py-6 lg:grid-cols-2">
      {/* Search + results */}
      <div className="flex min-h-0 flex-col">
        <h1 className="mb-3 font-display text-xl font-bold text-ink-900">Cari Pesanan</h1>
        <div className="space-y-2">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-ink-400" />
              <Input
                data-scanner-target
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && run(term)}
                placeholder="Nomor invoice atau scan barcode…"
                className="pl-12 pr-11"
                autoFocus
              />
              {term && (
                <button
                  type="button"
                  onClick={clearSearch}
                  aria-label="Bersihkan pencarian"
                  className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-ink-400 transition-colors hover:bg-secondary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Button onClick={() => run(term)}>Cari</Button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-ink-400">Tampilkan</span>
            <Tabs value={scope} onValueChange={(v) => setScope(v as 'own' | 'all')}>
              <TabsList>
                <TabsTrigger value="own">Milik saya</TabsTrigger>
                <TabsTrigger value="all">Semua kasir</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-ink-400">Tanggal</span>
            <DatePickerField
              value={date}
              onChange={setDate}
              max={todayISO()}
              placeholder="Semua tanggal"
              className="h-9"
            />
            {date && (
              <button
                type="button"
                onClick={() => setDate('')}
                className="text-xs font-medium text-primary hover:underline"
              >
                Hapus filter
              </button>
            )}
            {total > 0 && (
              <span className="ml-auto text-xs text-ink-400">{total} pesanan</span>
            )}
          </div>
        </div>

        <ScrollArea className="mt-4 flex-1">
          {isFetching ? (
            <div className="flex justify-center py-12 text-ink-400">
              <Loader2 className="h-7 w-7 animate-spin" />
            </div>
          ) : activeQuery && rows.length === 0 ? (
            <EmptyState
              icon={FileSearch}
              title="Tidak ditemukan"
              description={`Tidak ada pesanan untuk "${activeQuery}".`}
            />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={FileSearch}
              title="Belum ada pesanan"
              description={
                date
                  ? 'Tidak ada transaksi pada tanggal ini. Ganti tanggal atau lihat semua tanggal.'
                  : scope === 'all'
                    ? 'Belum ada transaksi ticketbox.'
                    : 'Belum ada transaksi pada akun ini.'
              }
            />
          ) : (
            <ul className="space-y-2 pr-2">
              {rows.map((row) => (
                <li key={row.invoiceId}>
                  <button
                    type="button"
                    onClick={() => openDetail(row)}
                    className={cn(
                      'flex w-full items-center justify-between gap-3 rounded-lg border-2 bg-card px-4 py-3 text-left transition-colors hover:border-primary/40',
                      selected?.invoiceId === row.invoiceId ? 'border-primary' : 'border-border',
                    )}
                  >
                    <div className="min-w-0">
                      <p className="tnum truncate font-display font-semibold text-ink-900">
                        {row.invoiceId}
                      </p>
                      <p className="truncate text-xs text-ink-400">
                        {row.userInfo?.name ?? '—'} · {formatDateTime(row.createdAt)}
                      </p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1">
                        {row.paymentGateway && (
                          <span className={cn(CHIP_BASE, paymentChipClass(row.paymentGateway))}>
                            {PAYMENT_LABEL[String(row.paymentGateway).toLowerCase()] ??
                              String(row.paymentGateway)}
                          </span>
                        )}
                        {row.posType && (
                          <span className={cn(CHIP_BASE, 'bg-amber-100 text-amber-700')}>
                            {CHANNEL_LABEL[String(row.posType).toLowerCase()] ?? String(row.posType)}
                          </span>
                        )}
                        {row.cashierName && (
                          <span className={cn(CHIP_BASE, 'bg-slate-100 text-slate-600')}>
                            <UserRound className="h-2.5 w-2.5" />
                            {row.cashierName}
                          </span>
                        )}
                        {row.printStatus && (
                          <span className={cn(CHIP_BASE, printChipClass(row.printStatus))}>
                            <Printer className="h-2.5 w-2.5" />
                            {PRINT_STATUS_LABEL[row.printStatus] ?? row.printStatus}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <MoneyValue value={row.totalAmount} className="text-sm" />
                      <StatusPill status={row.status} />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>

        {totalPage > 1 && (
          <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              disabled={page <= 1 || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="h-4 w-4" /> Sebelumnya
            </Button>
            <span className="text-xs text-ink-400">
              Halaman {page} dari {totalPage}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              disabled={page >= totalPage || isFetching}
              onClick={() => setPage((p) => Math.min(totalPage, p + 1))}
            >
              Berikutnya <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Detail + reprint */}
      <div className="flex min-h-0 flex-col">
        <h2 className="mb-3 font-display text-xl font-bold text-ink-900">Detail</h2>
        <Card className="flex min-h-0 flex-1 flex-col">
          <CardContent className="flex min-h-0 flex-1 flex-col pt-5">
            {detailLoading ? (
              <div className="flex flex-1 items-center justify-center text-ink-400">
                <Loader2 className="h-7 w-7 animate-spin" />
              </div>
            ) : !selected ? (
              <EmptyState
                title="Belum ada pesanan dipilih"
                description="Pilih pesanan dari hasil pencarian untuk melihat detail dan mencetak ulang."
                className="flex-1 border-0 bg-transparent"
              />
            ) : (
              <>
                <ScrollArea className="flex-1">
                  <ReceiptPreview detail={selected} />
                </ScrollArea>
                {/* Print-only clone — the browser prints just this subtree
                    (see the .printing-receipt rules in index.css). */}
                <div id="receipt-print" aria-hidden="true" className="hidden">
                  <ReceiptPreview detail={selected} />
                </div>
                <Separator className="my-4" />
                {unpaid && (
                  // Sits ABOVE the print actions on purpose: the first thing to
                  // do with an unpaid order is take the money. Printing is
                  // pointless until then — the tickets are only minted on
                  // settlement.
                  <Button
                    size="lg"
                    className="mb-2 h-14 w-full gap-2 text-lg"
                    onClick={() => setResumeOpen(true)}
                  >
                    <Wallet className="h-5 w-5" />
                    Lanjutkan Pembayaran
                  </Button>
                )}
                <div className="flex gap-2">
                  <Button
                    size="lg"
                    variant={unpaid ? 'outline' : 'default'}
                    className="h-14 flex-1 gap-2 text-lg"
                    onClick={reprint}
                    disabled={reprintDisabled || printing}
                  >
                    {printing ? <Loader2 className="h-5 w-5 animate-spin" /> : <Printer className="h-5 w-5" />}
                    Cetak Ulang Tiket
                    <kbd className="ml-1 rounded border border-primary-foreground/30 px-1.5 text-xs">
                      Ctrl+P
                    </kbd>
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-14 gap-2 text-base"
                    onClick={printReceipt}
                  >
                    <Receipt className="h-5 w-5" />
                    Cetak Struk
                  </Button>
                </div>
                {ticketGroups.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {ticketGroups.map((g) => (
                      <Button
                        key={g.key}
                        variant="outline"
                        size="sm"
                        disabled={!printerBound}
                        onClick={() => reprintGroup(g.ids, g.label)}
                      >
                        {g.label}
                        {g.ids.length > 1 && (
                          <span className="tnum ml-1 text-ink-400">×{g.ids.length}</span>
                        )}
                      </Button>
                    ))}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    {selected && (
        <ResumePaymentDialog
          open={resumeOpen}
          onOpenChange={setResumeOpen}
          invoiceId={selected.invoiceId}
          total={selected.totalAmount}
          onPaid={(inv) => {
            // The sale now has tickets: refresh the list so the row reads
            // SUCCESS, and reload the detail the print buttons act on.
            void refetchOrders();
            fetchDetail(inv, { onSuccess: (d) => setSelected(d) });
            toast.success('Pembayaran diterima — tiket siap dicetak.');
          }}
        />
      )}
    </div>
  );
}
